import { Button, DatePicker, Select, Space, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/shared/components/DataTable";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { useEmployeeNames, withSelectedEmployeeOption } from "@/shared/hooks/useEmployeeNames";
import { DayDetailDrawer } from "../components/DayDetailDrawer";
import {
	useAttendanceDays,
	useAttendanceDepartmentOptions,
	useAttendanceEmployeeOptions,
	useAttendanceZoneOptions,
} from "../hooks/useAttendance";
import {
	ATTENDANCE_STATUS_COLORS,
	ATTENDANCE_STATUS_VALUES,
	type AttendanceDay,
	type AttendanceStatus,
} from "../types";
import { isPreliminary } from "../utils/preliminary";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const DEFAULT_RANGE_DAYS = 6;
const EMPLOYEE_SEARCH_DEBOUNCE_MS = 300;

/** ISO timestamptz → mahalliy "HH:mm". Bo'sh qiymat uchun «—». */
function formatTime(value: string | null): string {
	return value ? dayjs(value).format("HH:mm") : "—";
}

export function AttendanceDaysPage() {
	const { t } = useTranslation("attendance");
	// ⚠️ `ATTENDANCE_STATUS_LABELS` (`../types`) BU YERDA ATAYLAB
	// ISHLATILMAYDI — u `modules/employees` bilan bo'lishiladigan
	// TARJIMASIZ meros obyekt (izohga qarang). Shu modulning O'Z filtri
	// `t()` orqali tarjima qilinadi.
	const statusOptions = ATTENDANCE_STATUS_VALUES.map((value) => ({
		value,
		label: t(`status.${value}`),
	}));
	const [range, setRange] = useState<[Dayjs, Dayjs]>([
		dayjs().subtract(DEFAULT_RANGE_DAYS, "day"),
		dayjs(),
	]);
	const [employeeId, setEmployeeId] = useState<string | undefined>(undefined);
	const [departmentId, setDepartmentId] = useState<string | undefined>(undefined);
	const [zoneId, setZoneId] = useState<string | undefined>(undefined);
	const [status, setStatus] = useState<AttendanceStatus | undefined>(undefined);
	const [employeeSearch, setEmployeeSearch] = useState("");
	const [openDayId, setOpenDayId] = useState<string | null>(null);
	const debouncedEmployeeSearch = useDebouncedValue(employeeSearch, EMPLOYEE_SEARCH_DEBOUNCE_MS);

	const { data: employees = [], isFetching: isEmployeeSearchLoading } =
		useAttendanceEmployeeOptions(debouncedEmployeeSearch);
	// Spec §6.5: «sana + bo'lim/hudud filtri».
	const { data: departments = [] } = useAttendanceDepartmentOptions();
	const { data: zonesList = [] } = useAttendanceZoneOptions();

	const query = useAttendanceDays({
		from: range[0].format("YYYY-MM-DD"),
		to: range[1].format("YYYY-MM-DD"),
		employeeId,
		departmentId,
		zoneId,
		status,
	});

	// ⚠️ Ism KO'RSATILAYOTGAN QATORLARDAN olinadi, filtr Select'ining
	// opsiyalaridan EMAS: opsiyalar `limit: 100` bilan va `q` bo'yicha
	// filtrlangan, ya'ni qidiruvga bitta harf yozilishi bilan jadval xom
	// UUID ga aylanardi (1c da o'lchangan).
	const employeeNames = useEmployeeNames([...query.items.map((row) => row.employeeId), employeeId]);

	const columns: ColumnsType<AttendanceDay> = [
		{ title: t("page.columns.date"), dataIndex: "workDate", key: "workDate" },
		{
			title: t("page.columns.employee"),
			key: "employeeId",
			render: (_, row) => employeeNames.get(row.employeeId) ?? row.employeeId,
		},
		{
			title: t("page.columns.status"),
			key: "status",
			render: (_, row) => (
				<Space size={4}>
					<Tag color={ATTENDANCE_STATUS_COLORS[row.status]}>{t(`status.${row.status}`)}</Tag>
					{isPreliminary(row) ? <Tag color="default">{t("page.preliminaryTag")}</Tag> : null}
				</Space>
			),
		},
		{
			title: t("page.columns.checkIn"),
			key: "in",
			render: (_, row) => formatTime(row.firstCheckInAt),
		},
		{
			title: t("page.columns.checkOut"),
			key: "out",
			render: (_, row) => formatTime(row.lastCheckOutAt),
		},
		{ title: t("page.columns.lateMinutes"), dataIndex: "lateMinutes", key: "lateMinutes" },
		{ title: t("page.columns.earlyMinutes"), dataIndex: "earlyMinutes", key: "earlyMinutes" },
		{ title: t("page.columns.inZoneMinutes"), dataIndex: "timeInZoneMin", key: "timeInZoneMin" },
		{ title: t("page.columns.departures"), dataIndex: "departuresCount", key: "departuresCount" },
		{
			title: "",
			key: "actions",
			render: (_, row) => (
				<Button type="link" onClick={() => setOpenDayId(row.id)}>
					{t("page.detailButton")}
				</Button>
			),
		},
	];

	return (
		<div>
			<Title level={3} className="mb-6">
				{t("page.title")}
			</Title>

			<Space wrap className="mb-4">
				<RangePicker
					value={range}
					allowClear={false}
					onChange={(value) => {
						if (value?.[0] && value[1]) {
							setRange([value[0], value[1]]);
							query.setPage(1);
						}
					}}
				/>
				<Select
					allowClear
					placeholder={t("page.filters.employeePlaceholder")}
					style={{ width: 240 }}
					options={withSelectedEmployeeOption(
						employees.map((employee) => ({ value: employee.id, label: employee.fullName })),
						employeeId,
						employeeNames
					)}
					value={employeeId}
					showSearch={{ filterOption: false, onSearch: setEmployeeSearch }}
					loading={isEmployeeSearchLoading}
					notFoundContent={
						isEmployeeSearchLoading
							? t("page.filters.employeeSearching")
							: t("page.filters.employeeNotFound")
					}
					onChange={(value) => {
						setEmployeeId(value);
						query.setPage(1);
					}}
				/>
				{/* ⚠️ Bo'lim filtri AVLODLARSIZ — aniq bo'lim (Task 7 sxemasidagi
				    `departmentId` izohi). Ierarxiya bo'yicha kengaytirish
				    `resolveScope` ning ishi va u ko'lam uchun, filtr uchun emas. */}
				<Select
					allowClear
					placeholder={t("page.filters.departmentPlaceholder")}
					style={{ width: 220 }}
					options={departments.map((dept) => ({ value: dept.id, label: dept.name }))}
					value={departmentId}
					showSearch={{ optionFilterProp: "label" }}
					onChange={(value) => {
						setDepartmentId(value);
						query.setPage(1);
					}}
				/>
				{/* ⚠️ Hudud filtri SHU KUNDAGI biriktirishga qaraydi
				    (`employee_zones` APPEND-ONLY, Task 7 dagi `zoneCondition`). */}
				<Select
					allowClear
					placeholder={t("page.filters.zonePlaceholder")}
					style={{ width: 220 }}
					options={zonesList.map((zone) => ({ value: zone.id, label: zone.name }))}
					value={zoneId}
					showSearch={{ optionFilterProp: "label" }}
					onChange={(value) => {
						setZoneId(value);
						query.setPage(1);
					}}
				/>
				<Select
					allowClear
					placeholder={t("page.filters.statusPlaceholder")}
					style={{ width: 180 }}
					options={statusOptions}
					value={status}
					onChange={(value) => {
						setStatus(value);
						query.setPage(1);
					}}
				/>
			</Space>

			<DataTable<AttendanceDay> columns={columns} query={query} />

			<DayDetailDrawer dayId={openDayId} onClose={() => setOpenDayId(null)} />
		</div>
	);
}
